import PageBackTitle from "../Component/PageBackTitle";
import React, { useState, useEffect } from "react";
import {
  Download,
  Filter,
  Loader2,
  FileText,
  Users,
  TrendingDown,
  TrendingUp,
  Wallet,
} from "lucide-react";
import axios from "axios";
import API_URL from "../Config";
import "./expense.css";
import Select from "react-select";

/* ─── Stat Card ─────────────────────────────────────────────────────────── */
const StatCard = ({ title, value, subtitle, tone = "primary", Icon }) => (
  <div className={`rpt-stat rpt-stat--${tone}`}>
    {Icon && <Icon size={18} className="rpt-stat__icon" />}
    <div className="rpt-stat__label">{title}</div>
    <div className="rpt-stat__value">{value}</div>
    {subtitle && <div className="rpt-stat__sub">{subtitle}</div>}
  </div>
);

/* ─── Category row ──────────────────────────────────────────────────────── */
const CategoryBar = ({ label, amount, total }) => {
  const pct = total > 0 ? ((amount / total) * 100).toFixed(1) : 0;
  return (
    <div className="rpt-cat-row">
      <div className="rpt-cat-row__name">{label}</div>
      <div className="rpt-cat-row__bar-wrap">
        <div className="rpt-cat-row__bar" style={{ width: `${pct}%` }} />
      </div>
      <div className="rpt-cat-row__pct">{pct}%</div>
      <div className="rpt-cat-row__amt">
        ₹
        {parseFloat(amount).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
        })}
      </div>
    </div>
  );
};

/* ─── Main ──────────────────────────────────────────────────────────────── */
const ExpenseReports = ({ isSidebarExpanded }) => {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filterExportLoading, setFilterExportLoading] = useState(false); // NEW
  const [reports, setReports] = useState([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [reportPage, setReportPage] = useState(1);
  const reportPerPage = 20;

  // Active tab: "list" | "detail"
  const [activeTab, setActiveTab] = useState("list");

  // Selected row for detail
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailReport, setDetailReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filters, setFilters] = useState({
    report_month: new Date().toISOString().slice(0, 7),
    user_id: "",
  });
  const [users, setUsers] = useState([]);
  const getAuthKey = () => localStorage.getItem("authKey") || "";
  const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  /* ── Users ── */
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/get_user`, {
          headers: { Authorization: getAuthKey() },
          params: { per_page: 200 },
        });
        const d = res.data;
        setUsers(d.users || d.data || d || []);
      } catch {
        setUsers([]);
      }
    })();
  }, []);

  /* ── Reports list ── */
  const fetchReports = async (pg = reportPage, fs = filters) => {
    setLoading(true);
    try {
      const params = { page: pg, per_page: reportPerPage };
      if (fs.report_month) params.report_month = fs.report_month;
      if (fs.user_id) params.user_id = fs.user_id;
      const res = await axios.get(`${API_URL}/api/expense/reports`, {
        headers: { Authorization: getAuthKey() },
        params,
      });
      const data = res.data;
      setReports(Array.isArray(data.reports) ? data.reports : []);
      setReportTotal(data.total ?? 0);
    } catch {
      console.error("Error fetching reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(reportPage, filters);
  }, [reportPage]); // eslint-disable-line

  /* ── Auto-apply filters on change ── */
  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);
    setReportPage(1);
    fetchReports(1, updatedFilters);
  };

  /* ── Detail ── */
  const fetchDetail = async (report) => {
    setSelectedReport(report);
    setDetailReport(null);
    setDetailLoading(true);
    setActiveTab("detail");
    try {
      const res = await axios.get(`${API_URL}/api/expense/report`, {
        headers: { Authorization: getAuthKey() },
        params: { report_month: report.report_month, user_id: report.user_id },
      });
      setDetailReport(res.data);
    } catch {
      setDetailReport(null);
    } finally {
      setDetailLoading(false);
    }
  };

  /* ── Export (per-row / detail tab) ── */
  const handleExport = async (report) => {
    const target = report || selectedReport;
    if (!target) return;
    setExportLoading(true);
    try {
      const params = new URLSearchParams({ report_month: target.report_month });
      if (target.user_id) params.append("user_id", target.user_id);
      const response = await fetch(
        `${API_URL}/api/expense/report/export?${params}`,
        {
          headers: { Authorization: getAuthKey() },
        },
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `Expense_${(target.employee_name || "Report").replace(/\s/g, "_")}_${target.report_month}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const err = await response.json();
        alert("Export failed: " + (err.message || "Unknown error"));
      }
    } catch {
      alert("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  /* ── NEW: Export using current filter values (near Clear button) ── */
  const handleFilterExport = async () => {
    const exportMonth = filters.report_month || selectedReport?.report_month;
    const exportUserId = filters.user_id || selectedReport?.user_id;
    const exportUserName =
      users.find((u) => String(u.ID || u.id) === String(exportUserId))?.Name ||
      users.find((u) => String(u.ID || u.id) === String(exportUserId))?.name ||
      selectedReport?.employee_name ||
      detailReport?.employee?.name ||
      "Employee";

    if (!exportMonth) {
      alert("Please select a report month to export.");
      return;
    }

    if (!exportUserId) {
      alert("Select an employee or open a report row before exporting.");
      return;
    }

    setFilterExportLoading(true);
    try {
      const params = new URLSearchParams({
        report_month: exportMonth,
        user_id: exportUserId,
      });

      const response = await fetch(
        `${API_URL}/api/expense/report/export?${params}`,
        {
          headers: { Authorization: getAuthKey() },
        },
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;

        a.download = `Expense_${exportUserName.replace(/\s/g, "_")}_${exportMonth}.xlsx`;

        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const err = await response.json();
        alert("Export failed: " + (err.message || "Unknown error"));
      }
    } catch {
      alert("Export failed. Please try again.");
    } finally {
      setFilterExportLoading(false);
    }
  };

  const handleClear = () => {
    const fs = {
      report_month: new Date().toISOString().slice(0, 7),
      user_id: "",
    };
    setFilters(fs);
    setReportPage(1);
    fetchReports(1, fs);
  };

  /* ── Helpers ── */
  const fmtCur = (val) =>
    val != null
      ? `₹${parseFloat(val).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
      : "—";

  const fmtSignedCur = (val) => {
    const amount = toNumber(val);
    return `${amount > 0 ? "+" : amount < 0 ? "-" : ""}${fmtCur(Math.abs(amount))}`;
  };

  const fmtDate = (val) => {
    if (!val) return "—";
    try {
      return new Date(val).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return val;
    }
  };

  const fmtMonth = (val) => {
    if (!val) return "—";
    try {
      return new Date(val + "-01").toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
    } catch {
      return val;
    }
  };

  /* ── Overview stats ── */
  const totExpense = reports.reduce((s, r) => s + toNumber(r.total_expense), 0);
  const totAdvance = reports.reduce((s, r) => s + toNumber(r.advance_amount), 0);
  const totPrevBalance = reports.reduce((s, r) => s + toNumber(r.prev_balance_cf), 0);
  const totEffectiveAdvance = reports.reduce(
    (s, r) =>
      s +
      toNumber(
        r.effective_advance ??
          toNumber(r.advance_amount) + toNumber(r.prev_balance_cf),
      ),
    0,
  );
  const totItemsCnt = reports.reduce(
    (s, r) => s + parseInt(r.item_count || 0),
    0,
  );
  const balanceTot = totEffectiveAdvance - totExpense;

  const summary = detailReport?.summary;
  const employee = detailReport?.employee;
  const advance = detailReport?.advance;
  const detailItems = detailReport?.items || [];

  const employeeOptions = [
    { value: "", label: "All Employees" },
    ...users.map((u) => ({
      value: String(u.ID || u.id),
      label: u.Name || u.name || "Unnamed Employee",
    })),
  ];

  const employeeSelectStyles = {
    control: (base, state) => ({
      ...base,
      width: "100% !important",
      minHeight: "36px",
      borderColor: state.isFocused ? "#0075b8" : "#d1d5db",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#0075b8",
      },
      fontSize: "12px",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      fontSize: "12px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  return (
    <div
      className={`data-list-content-container page-content ${isSidebarExpanded ? "expanded" : "collapsed"}`}
    >
      <div
        className="data-list-content"
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* ── Page header ── */}
        <div className="data-list-header">
          <div className="data-list-header-content">
            <PageBackTitle>Expense Reports</PageBackTitle>
            <div className="data-list-heading-content-right-side">
              {activeTab === "detail" && selectedReport && (
                <button
                  className="exp-btn exp-btn--primary exp-btn--sm"
                  onClick={() => handleExport(selectedReport)}
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <>
                      <span className="exp-spinner exp-spinner--sm" />{" "}
                      Exporting…
                    </>
                  ) : (
                    <>
                      <Download size={13} /> Export Excel
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="data-list-table" style={{ padding: "10px 20px" }}>
          <div className="rpt-tab-bar">
            <button
              className={`rpt-tab ${activeTab === "list" ? "rpt-tab--active" : ""}`}
              onClick={() => setActiveTab("list")}
            >
              <Users size={14} /> Summary List
            </button>
            <button
              className={`rpt-tab ${activeTab === "detail" ? "rpt-tab--active" : ""}`}
              onClick={() => {
                if (selectedReport) setActiveTab("detail");
              }}
              disabled={!selectedReport}
              title={
                !selectedReport ? "Click a report row to view details" : ""
              }
            >
              <FileText size={14} />
              Detailed Report
              {selectedReport && (
                <span className="rpt-tab__chip">
                  {selectedReport.employee_name}
                </span>
              )}
            </button>
          </div>

          <div className="rpt-body">
            {/* ════════════════════ LIST TAB ════════════════════ */}
            {activeTab === "list" && (
              <div className="rpt-list-panel">
                {/* Filters */}
                <div className="rpt-filter-bar">
                  <div className="rpt-filter-bar__inner">
                    <Filter size={14} className="rpt-filter-bar__icon" />
                    <span className="rpt-filter-bar__title">Filters</span>

                    <div className="rpt-filter-bar__fields">
                      <div className="rpt-filter-field">
                        <label className="exp-label">Month</label>
                        <input
                          type="month"
                          className="exp-input exp-input--sm"
                          value={filters.report_month}
                          onChange={(e) =>
                            handleFilterChange("report_month", e.target.value)
                          }
                        />
                      </div>
                      {users.length > 0 && (
                        <div className="rpt-filter-field">
                          <label className="exp-label">Advance Given To</label>
                          <Select
                            options={employeeOptions}
                            value={
                              employeeOptions.find(
                                (opt) =>
                                  String(opt.value) === String(filters.user_id),
                              ) || employeeOptions[0]
                            }
                            onChange={(selected) =>
                              handleFilterChange(
                                "user_id",
                                selected?.value || "",
                              )
                            }
                            isSearchable
                            isClearable={false}
                            placeholder="Search employee..."
                            styles={employeeSelectStyles}
                            menuPortalTarget={document.body}
                            classNamePrefix="exp-react-select"
                            className="report_exep_select"
                          />
                        </div>
                      )}
                    </div>

                    {/* ── Actions: Clear + Export Excel ── */}
                    <div className="rpt-filter-bar__actions">
                      <button
                        className="exp-btn exp-btn--ghost exp-btn--sm"
                        onClick={handleClear}
                      >
                        Clear
                      </button>
                      <button
                        className="exp-btn exp-btn--primary exp-btn--sm"
                        onClick={handleFilterExport}
                        disabled={filterExportLoading}
                        title={
                          filters.user_id || selectedReport?.user_id
                            ? "Export selected employee's report"
                            : "Select an employee or open a report first"
                        }
                      >
                        {filterExportLoading ? (
                          <>
                            <span className="exp-spinner exp-spinner--sm" />{" "}
                            Exporting…
                          </>
                        ) : (
                          <>
                            <Download size={13} /> Export Excel
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Overview stats */}
                {!loading && reports.length > 0 && (
                  <>
                    <div className="rpt-section-title">
                      Overview
                      {filters.report_month && (
                        <span className="rpt-section-title__sub">
                          {" "}
                          — {fmtMonth(filters.report_month)}
                        </span>
                      )}
                    </div>
                    <div className="rpt-stats-grid">
                      <StatCard
                        title="Total Reports"
                        value={reportTotal}
                        subtitle={`${reports.length} shown`}
                        tone="primary"
                        Icon={Users}
                      />
                      <StatCard
                        title="Total Expenses"
                        value={fmtCur(totExpense)}
                        subtitle={`${totItemsCnt} items`}
                        tone="danger"
                        Icon={TrendingDown}
                      />
                      <StatCard
                        title="Total Advances"
                        value={fmtCur(totAdvance)}
                        subtitle="Current month only"
                        tone="warning"
                        Icon={Wallet}
                      />
                      <StatCard
                        title="Effective Advance"
                        value={fmtCur(totEffectiveAdvance)}
                        subtitle={`Includes prev. C/F ${fmtCur(totPrevBalance)}`}
                        tone="primary"
                        Icon={TrendingUp}
                      />
                      <StatCard
                        title={balanceTot >= 0 ? "Balance C/F" : "Overspent"}
                        value={fmtCur(Math.abs(balanceTot))}
                        subtitle={
                          balanceTot >= 0
                            ? "Returned to company"
                            : "To be reimbursed"
                        }
                        tone={balanceTot >= 0 ? "success" : "danger"}
                        Icon={TrendingUp}
                      />
                    </div>
                  </>
                )}

                {/* Table */}
                {loading ? (
                  <div className="rpt-loading">
                    <Loader2 size={24} className="rpt-loading__spin" />
                    <span>Loading reports…</span>
                  </div>
                ) : reports.length === 0 ? (
                  <div className="rpt-empty">
                    <FileText size={40} className="rpt-empty__icon" />
                    <p className="rpt-empty__title">No expense reports found</p>
                    <p className="rpt-empty__sub">
                      Try adjusting the month or employee filter.
                    </p>
                  </div>
                ) : (
                  <>
                    <div
                      className="rpt-section-title"
                      style={{ marginTop: 20 }}
                    >
                      Report List
                      <span className="rpt-section-title__sub">
                        {" "}
                        — click a row to view details
                      </span>
                    </div>
                    <div className="rpt-table-wrap">
                      <table className="rpt-table table multilevel-data">
                        <thead>
                          <tr>
                            <th>SR.</th>
                            <th>ADVANCE GIVEN TO</th>
                            <th>EMAIL</th>
                            <th>MONTH</th>
                            <th>TOTAL EXPENSE</th>
                            <th>ADVANCE</th>
                            <th>PREV. C/F</th>
                            <th>EFFECTIVE ADV.</th>
                            <th>NET</th>
                            <th>ITEMS</th>
                            <th>PERIOD</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports.map((r, idx) => {
                            const currentAdvance = toNumber(r.advance_amount);
                            const previousBalance = toNumber(r.prev_balance_cf);
                            const effectiveAdvance = toNumber(
                              r.effective_advance ??
                                currentAdvance + previousBalance,
                            );
                            const bal = effectiveAdvance - toNumber(r.total_expense);
                            const isActive =
                              selectedReport?.user_id === r.user_id &&
                              selectedReport?.report_month === r.report_month;
                            return (
                              <tr
                                key={`${r.user_id}-${r.report_month}`}
                                className={`hoverable-row rpt-table__row ${isActive ? "rpt-table__row--active" : ""}`}
                                onClick={() => fetchDetail(r)}
                              >
                                <td className="center-alignment">
                                  {(reportPage - 1) * reportPerPage + idx + 1}
                                </td>
                                <td className="rpt-cell--name">
                                  {r.employee_name || "—"}
                                </td>
                                <td className="rpt-cell--muted">
                                  {r.employee_email || "—"}
                                </td>
                                <td>{r.report_month || "—"}</td>
                                <td className="rpt-cell--danger">
                                  {fmtCur(r.total_expense)}
                                </td>
                                <td className="rpt-cell--warning">
                                  {fmtCur(r.advance_amount)}
                                </td>
                                <td className="rpt-cell--muted">
                                  {fmtCur(r.prev_balance_cf)}
                                </td>
                                <td className="rpt-cell--warning">
                                  {fmtCur(effectiveAdvance)}
                                </td>
                                <td>
                                  <span
                                    className={`exp-balance-chip ${bal >= 0 ? "exp-balance-chip--pos" : "exp-balance-chip--neg"}`}
                                  >
                                    {fmtSignedCur(bal)}
                                  </span>
                                </td>
                                <td className="center-alignment">
                                  <span className="exp-count-badge exp-count-badge--active">
                                    {r.item_count ?? 0}
                                  </span>
                                </td>
                                <td
                                  className="rpt-cell--muted"
                                  style={{ fontSize: 11 }}
                                >
                                  {r.period_start && r.period_end
                                    ? `${fmtDate(r.period_start)} – ${fmtDate(r.period_end)}`
                                    : "—"}
                                </td>
                                <td
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExport(r);
                                  }}
                                >
                                  <button
                                    className="exp-icon-btn"
                                    title="Export Excel"
                                  >
                                    <Download size={13} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {reportTotal > reportPerPage && (
                      <div className="rpt-pagination">
                        <button
                          className="exp-btn exp-btn--ghost exp-btn--sm"
                          disabled={reportPage === 1}
                          onClick={() => setReportPage((p) => p - 1)}
                        >
                          Prev
                        </button>
                        <span className="rpt-pagination__info">
                          Page {reportPage} of{" "}
                          {Math.ceil(reportTotal / reportPerPage)}
                        </span>
                        <button
                          className="exp-btn exp-btn--ghost exp-btn--sm"
                          disabled={
                            reportPage >= Math.ceil(reportTotal / reportPerPage)
                          }
                          onClick={() => setReportPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ════════════════════ DETAIL TAB ════════════════════ */}
            {activeTab === "detail" && (
              <div className="rpt-detail-panel">
                {detailLoading ? (
                  <div className="rpt-loading" style={{ paddingTop: 60 }}>
                    <Loader2 size={28} className="rpt-loading__spin" />
                    <span>Loading report…</span>
                  </div>
                ) : !detailReport ? (
                  <div className="rpt-empty">
                    <FileText size={40} className="rpt-empty__icon" />
                    <p className="rpt-empty__title">No report data</p>
                    <p className="rpt-empty__sub">
                      Go to Summary List and click a row to load details.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Detail header */}
                    <div className="rpt-detail-header">
                      <div>
                        <h2 className="rpt-detail-header__title">
                          {employee?.name || "Employee"} —{" "}
                          {detailReport.report_month}
                        </h2>
                        <p className="rpt-detail-header__sub">
                          {employee?.designation || ""}
                          {employee?.designation && employee?.email
                            ? " · "
                            : ""}
                          {employee?.email || ""}
                        </p>
                      </div>
                    </div>

                    {/* Employee + Advance info */}
                    <div className="rpt-info-cards">
                      <div className="rpt-info-card">
                        <div className="rpt-info-card__title">
                          Employee Info
                        </div>
                        <div className="rpt-info-card__rows">
                          {[
                            ["Name", employee?.name],
                            ["Email", employee?.email],
                            ["Designation", employee?.designation],
                          ]
                            .filter(([, v]) => v != null && v !== "")
                            .map(([k, v]) => (
                              <div key={k} className="rpt-info-card__row">
                                <span className="rpt-info-card__key">{k}</span>
                                <span className="rpt-info-card__val">{v}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                      <div className="rpt-info-card">
                        <div className="rpt-info-card__title">Advance Info</div>
                        <div className="rpt-info-card__rows">
                          {[
                            ["Month", advance?.advance_month || detailReport?.report_month],
                            [
                              "Amount",
                              advance?.amount != null
                                ? `₹${parseFloat(advance.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                                : null,
                            ],
                            ["Given By", advance?.given_by],
                            ["Location", advance?.location],
                            ["Notes", advance?.notes],
                          ].map(
                            ([k, v]) =>
                              v && (
                                <div key={k} className="rpt-info-card__row">
                                  <span className="rpt-info-card__key">
                                    {k}
                                  </span>
                                  <span className="rpt-info-card__val">
                                    {v}
                                  </span>
                                </div>
                              ),
                          )}
                        </div>
                      </div>
                      <div className="rpt-info-card">
                        <div className="rpt-info-card__title">
                          Balance Statement
                        </div>
                        <div className="rpt-info-card__rows">
                          {[
                            [
                              "Previous Balance C/F",
                              fmtCur(summary?.previous_balance_cf ?? 0),
                            ],
                            [
                              "Effective Advance",
                              fmtCur(summary?.effective_advance ?? 0),
                            ],
                            ["Total Expenses", fmtCur(summary?.total_expense ?? 0)],
                            [
                              (summary?.overspent_amount ?? 0) > 0
                                ? "Overspent Amount"
                                : "Balance Carried Forward",
                              fmtCur(
                                (summary?.overspent_amount ?? 0) > 0
                                  ? summary?.overspent_amount
                                  : summary?.balance_carried_forward,
                              ),
                            ],
                          ].map(([k, v]) => (
                            <div key={k} className="rpt-info-card__row">
                              <span className="rpt-info-card__key">{k}</span>
                              <span className="rpt-info-card__val">{v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Summary stats */}
                    <div className="rpt-section-title">Summary</div>
                    <div className="rpt-stats-grid">
                      <StatCard
                        title="Total Items"
                        value={summary?.total_items ?? 0}
                        subtitle="Expense entries"
                        tone="primary"
                      />
                      <StatCard
                        title="Total Expenses"
                        value={fmtCur(summary?.total_expense)}
                        subtitle="Sum of all items"
                        tone="danger"
                      />
                      <StatCard
                        title="Advance Given"
                        value={fmtCur(summary?.advance_amount)}
                        subtitle="Total advance"
                        tone="warning"
                      />
                      <StatCard
                        title="Previous Balance C/F"
                        value={fmtCur(summary?.previous_balance_cf)}
                        subtitle="Carried from prior month"
                        tone="primary"
                      />
                      <StatCard
                        title="Effective Advance"
                        value={fmtCur(summary?.effective_advance)}
                        subtitle="Advance + previous balance"
                        tone="primary"
                      />
                      {(summary?.overspent_amount ?? 0) > 0 ? (
                        <StatCard
                          title="Overspent"
                          value={fmtCur(summary?.overspent_amount)}
                          subtitle="To be reimbursed"
                          tone="danger"
                        />
                      ) : (
                        <StatCard
                          title="Balance C/F"
                          value={fmtCur(summary?.balance_carried_forward)}
                          subtitle="Returned to company"
                          tone="success"
                        />
                      )}
                    </div>

                    {/* Category breakdown */}
                    {summary?.category_totals &&
                      Object.keys(summary.category_totals).length > 0 && (
                        <>
                          <div
                            className="rpt-section-title"
                            style={{ marginTop: 20 }}
                          >
                            Category Breakdown
                          </div>
                          <div className="rpt-cat-panel">
                            {Object.entries(summary.category_totals)
                              .sort(([, a], [, b]) => b - a)
                              .map(([cat, amt]) => (
                                <CategoryBar
                                  key={cat}
                                  label={cat}
                                  amount={amt}
                                  total={summary.total_expense}
                                />
                              ))}
                          </div>
                        </>
                      )}

                    {/* Items table */}
                    {detailItems.length > 0 && (
                      <>
                        <div
                          className="rpt-section-title"
                          style={{ marginTop: 20 }}
                        >
                          Expense Items
                          <span className="rpt-section-title__sub">
                            {" "}
                            — {detailItems.length} item(s)
                          </span>
                        </div>
                        <div className="rpt-table-wrap">
                          <table className="rpt-table table multilevel-data">
                            <thead>
                              <tr>
                                <th>SR.</th>
                                <th>DATE</th>
                                <th>BILL / RECEIPT</th>
                                <th>AMOUNT</th>
                                <th>CATEGORY</th>
                                <th>PURPOSE / DESCRIPTION</th>
                                <th>VENDOR / REMARK</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detailItems.map((item, idx) => (
                                <tr key={item.id} className="hoverable-row">
                                  <td className="center-alignment">
                                    {idx + 1}
                                  </td>
                                  <td>{fmtDate(item.expense_date)}</td>
                                  <td>{item.bill_receipt_no || "—"}</td>
                                  <td className="rpt-cell--danger">
                                    {fmtCur(item.amount)}
                                  </td>
                                  <td>
                                    <span className="exp-cat-chip">
                                      {item.expense_category || "General"}
                                    </span>
                                  </td>
                                  <td>{item.purpose_description || "—"}</td>
                                  <td className="rpt-cell--muted">
                                    {item.vendor_remark || "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReports;
